import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Sparkles } from "lucide-react";
import { useApp } from "../context/AppContext";
import { CATEGORIES, SECTORS, SECTOR_CATEGORIES, sectorIdFromActivity } from "../data/mock";
import ProductCard from "../components/ProductCard";

const Catalogue = () => {
  const { t, tf, catalog, client } = useApp();
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState(params.get("cat") || "all");
  const [sector, setSector] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => { setActive(params.get("cat") || "all"); }, [params]);

  // Présélectionne le secteur du client connecté (si aucune catégorie explicite dans l'URL)
  useEffect(() => {
    if (client && client.activity && !params.get("cat")) {
      const sid = sectorIdFromActivity(client.activity);
      if (sid && SECTOR_CATEGORIES[sid]) setSector(sid);
    }
  }, [client, params]);

  const setCat = (id) => {
    setSector(null);
    setActive(id);
    if (id === "all") setParams({}); else setParams({ cat: id });
  };

  const pickSector = (id) => {
    setSector((cur) => (cur === id ? null : id));
    setActive("all");
    setParams({});
  };

  const sectorCats = sector ? (SECTOR_CATEGORIES[sector] || []) : null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalog.filter((p) => {
      const matchCat = sectorCats
        ? sectorCats.includes(p.category)
        : (active === "all" || p.category === active);
      const matchQ = !q || tf(p.name).toLowerCase().includes(q) || (p.vendor || "").toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [active, sector, sectorCats, query, tf, catalog]);

  return (
    <div className="pt-28 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-5">
        <span className="inline-flex items-center gap-2 bg-navy/5 text-navy font-display font-semibold text-xs px-4 py-1.5 rounded-full">
          <span className="w-2 h-2 rounded-full bg-orange" /> {t("catalogue_label")}
        </span>
        <h1 className="font-display font-black text-4xl md:text-5xl text-navy mt-4">{t("catalogue_title")}</h1>
        <p className="text-gray-600 mt-3 max-w-2xl">{t("catalogue_sub")}</p>

        {/* Search */}
        <div className="mt-8 relative max-w-md">
          <Search size={18} className="absolute top-1/2 -translate-y-1/2 ltr:left-4 rtl:right-4 text-gray-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("search_ph")}
            className="w-full ltr:pl-11 rtl:pr-11 ltr:pr-4 rtl:pl-4 py-3 rounded-full border border-gray-200 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none text-sm" />
        </div>

        {/* Sector quick picks */}
        <div className="mt-6">
          <p className="font-display font-semibold text-gray-500 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5"><Sparkles size={13} className="text-orange" /> {t("sector_pick")}</p>
          <div className="flex items-center gap-2 flex-wrap">
            {SECTORS.map((s) => (
              <button key={s.id} onClick={() => pickSector(s.id)} data-testid={`sector-pick-${s.id}`}
                className={`font-display font-semibold text-sm px-4 py-2 rounded-full border transition-colors ${sector === s.id ? "bg-orange text-white border-orange" : "bg-white text-navy border-gray-200 hover:border-orange"}`}>
                {tf(s)}
              </button>
            ))}
          </div>
          {sector && client && sectorIdFromActivity(client.activity) === sector && (
            <p className="mt-2 text-sm text-navy font-display" data-testid="sector-note">✨ {t("my_selection")} — {client.activity}</p>
          )}
        </div>

        {/* Category Filters */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <button onClick={() => setCat("all")}
            className={`font-display font-semibold text-sm px-4 py-2 rounded-full transition-colors ${active === "all" && !sector ? "bg-navy text-white" : "bg-navy/5 text-navy hover:bg-navy/10"}`}>{t("filter_all")}</button>
          {CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => setCat(c.id)}
              className={`font-display font-semibold text-sm px-4 py-2 rounded-full transition-colors ${active === c.id && !sector ? "bg-navy text-white" : "bg-navy/5 text-navy hover:bg-navy/10"}`}>{tf(c.short)}</button>
          ))}
        </div>

        <p className="mt-6 text-sm text-gray-500 font-display">{filtered.length} {filtered.length > 1 ? t("references") : t("reference")}</p>

        {filtered.length === 0 ? (
          <div className="py-24 text-center text-gray-400 font-display">{t("empty")}</div>
        ) : (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Catalogue;
